import ReqError from "@source/api/reqError";
import { api } from "@source/api/http";
import {
  IAddNewEmployeeRequest,
  IAddNewEmployeeResponse,
  IDeleteEmployeeRequest,
  IEditEmployeeRequest,
  IEditEmployeeResponse,
  IVerifyRequest,
  IVerifyResponse,
  IGetEmployeeRequest,
  IGetEmployeeResponse,
  IResetForgotPasswordRequest,
  IResetForgotPasswordResponse,
  ISendEmailForgotPasswordRequest,
  IGetEmployeesNamesResponse,
  IGetAgencyAccountsRequest,
  IAddNewAgencyAccountRequest, IEditEmployeeEmailRequest
} from "@source/api/models/employeeModels";
import { AxiosResponse } from "axios";

export default class EmploeeService {
  static async add(employee: IAddNewEmployeeRequest) {
    try {
      return (await api.post<IAddNewEmployeeResponse>('auth/employee/register', employee)).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.addNewEmployeeError(e.response)
    }
  }

  static async getNames(nursingHomeId: string) {
    try {
      return (await api.get<IGetEmployeesNamesResponse[]>(`employee/${nursingHomeId}/names`)).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.getNamesError(e.response)
    }
  }

  static async edit(employee: IEditEmployeeRequest, employeeId: string) {
    try {
      return (await api.put<IEditEmployeeResponse>(`employee/${employeeId}`, employee)).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.editEmployeeError(e.response)
    }
  }

  static async delete({employeeId, nursingHomeId}: IDeleteEmployeeRequest) {
    try {
      return (await api.delete(`employee?employeeId=${employeeId}&nursingHomeId=${nursingHomeId}`)).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.editEmployeeError(e.response)
    }
  }

  static async get({searchString = "", organizationId, isDisabled = "false", isConfirmed = "", limit = 100, page = 0, nursingHomeIds}: IGetEmployeeRequest) {
    try {
      return (
        await api.
        get<IGetEmployeeResponse>
        (`employee?searchString=${searchString}&organizationId=${organizationId}&isDisabled=${isDisabled}${isConfirmed.trim() && `&isConfirmed=${isConfirmed}`}&limit=${limit}&page=${page}`, {params: {
          nursingHomeIds
        }})
      ).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.editEmployeeError(e.response)
    }
  }

  static async verify(verifyInfo: IVerifyRequest) {
    try {
      return (await api.post<IVerifyResponse>('auth/employee/confirm', verifyInfo)).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.verifyEmployeeError(e.response)
    }
  }

  static async disableEmployee(employeeId: string) {
    try {
      return (await api.patch(`users/employees/${employeeId}/disable`, {})).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.disableEmployeeError(e.response)
    }
  }

  static async enableEmployee(employeeId: string) {
    try {
      return (await api.patch(`users/employees/${employeeId}/enable`, {})).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.enableEmployeeError(e.response)
    }
  }

  static async resetPassword(body: IResetForgotPasswordRequest) {
    try {
      return (await api.post<IResetForgotPasswordResponse>('auth/employee/forgot-password/reset', body)).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.resetPasswordEmployeeError(e.response)
    }
  }

  static async sendForgotPasswordCodeToEmail(body: ISendEmailForgotPasswordRequest) {
    try {
      return (await api.post<IResetForgotPasswordResponse>('auth/employee/forgot-password', body)).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.forgotPasswordEmployeeError(e.response)
    }
  }

  static async createEmployeePhoto(req: FormData, employeeId: string): Promise<AxiosResponse<any>> {
    try {
      return(await api.post(`employee/${employeeId}/photo`, req)).data;
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.createEmployeePhotoError(e.response)
    }
  }

  static async deleteEmployeePhoto(employeeId: string): Promise<AxiosResponse<any>> {
    try {
      return(await api.delete(`employee/${employeeId}/photo`)).data;
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.deleteEmployeePhotoError(e.response)
    }
  }

  static async getAgencyAccounts({searchString = "", organizationId, isDisabled = "false", limit = 100, page = 0, nursingHomeIds}: IGetAgencyAccountsRequest) {
    try {
      return (
        await api.
        get<IGetEmployeeResponse>
        (`agency?searchString=${searchString}&organizationId=${organizationId}&isDisabled=${isDisabled}&limit=${limit}&page=${page}`, {params: {
          nursingHomeIds
        }})
      ).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.getAgencyAccountsError(e.response)
    }
  }

  static async addNewAgencyAccount(agencyAccount: IAddNewAgencyAccountRequest) {
    try {
      return (await api.post<IAddNewEmployeeResponse>('auth/agency/register/root', agencyAccount)).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.addNewAgencyAccountError(e.response)
    }
  }

  static async disableAgencyAccount(agencyId: string) {
    try {
      return (await api.patch(`agency/${agencyId}/disable`, {})).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.disableAgencyAccountError(e.response)
    }
  }

  static async enableAgencyAccount(agencyId: string) {
    try {
      return (await api.patch(`agency/${agencyId}/enable`, {})).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.enableAgencyAccountError(e.response)
    }
  }

  static async changeEmail(request: IEditEmployeeEmailRequest) {
    try {
      return (await api.put<IResetForgotPasswordResponse>(`auth/employee/${request._id}/email`, { email: request.email })).data
    } catch (e: any) {
      const downError = ReqError.handleServerDownError(e);
      if(downError) {
        throw downError;
      }
      throw ReqError.editEmployeeError(e.response)
    }
  }
}